import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { ASSETS, BRAND, PRESS_KIT_BIOS } from "../data/siteContent";
import { Seo } from "../seo/Seo";
import { buildSchemas, webPageSchema, breadcrumbSchema, articleSchema } from "../seo/schemas";

const FACTS = [
  ["Born", "October 1, 1987"],
  ["Birthplace", "Santo Domingo, Dominican Republic"],
  ["Heritage", "Dominican-American"],
  ["Family", "Identical twin brother, Miguel Santana"],
  ["Hometowns", "New York · Atlanta · Boston"],
  ["Languages", "English · Spanish"],
  ["Podcast", <a key="podcast-link" href="https://proveyourpointpodcast.com/" target="_blank" rel="noopener noreferrer" data-testid="bio-podcast-link" className="link-underline text-[#0a0a0a]">The Prove Your Point Podcast</a>],
  ["Book", <a key="book-link" href="https://the12lawsofdatingdynamics.com/" target="_blank" rel="noopener noreferrer" data-testid="bio-book-link" className="link-underline text-[#0a0a0a]">The 12 Laws of Dating Dynamics</a>],
  [
    "IMDb",
    <a
      key="imdb"
      href="https://www.imdb.com/name/nm18563041/"
      target="_blank"
      rel="noreferrer"
      data-testid="bio-imdb-link"
      className="link-underline text-[#0a0a0a]"
    >
      Manuel Santana on IMDb
    </a>,
  ],
];

const TIMELINE = [
  { year: "1987", city: "Santo Domingo, DR", event: "Born October 1, alongside identical twin brother Miguel Santana." },
  { year: "1989", city: "New York City", event: "The Santana family immigrates to NYC in search of greater opportunity." },
  { year: "—",    city: "New York City", event: "Comes of age in the diverse, fast-paced culture of NYC. Falls in love with music, performance and influence." },
  { year: "Post-HS", city: "Atlanta, GA", event: "Family relocates south. Manuel enters the world of nightlife — promotion and security." },
  { year: "2013", city: "Boston, MA", event: "Returns to the Northeast. Pivots from music toward studying dating dynamics and relationship psychology." },
  { year: "2025", city: "—",          event: "Releases The 12 Laws of Dating Dynamics. Launches The Prove Your Point Podcast." },
  { year: "Today", city: BRAND.cities.join(" · "), event: "Continues building his platform as a media personality, author and podcast host." },
];

const Section = ({ overline, children }) => (
  <section className="border-t border-black/10 pt-12 md:pt-16">
    <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#C8102E] mb-8">— {overline}</p>
    <div className="space-y-6 text-black/75 text-base md:text-lg leading-[1.65]">{children}</div>
  </section>
);

export default function Bio() {
  return (
    <SiteLayout>
      <Seo
        title="The Manuel Santana Biography — From Santo Domingo to Social Commentary"
        description="The full biography of Manuel Santana — Dominican-American media personality, podcast host of The Prove Your Point Podcast and author of The 12 Laws of Dating Dynamics."
        path="/bio"
        type="article"
        schemas={buildSchemas([
          articleSchema({
            path: "/bio",
            headline: "The Manuel Santana Biography",
            description: "Full biography of Manuel Santana — Dominican-American media personality, podcast host, and author.",
            datePublished: "2025-01-15",
          }),
          webPageSchema({ path: "/bio", name: "Manuel Santana Biography", description: "Full biographical story of Manuel Santana." }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
            { name: "Biography", path: "/bio" },
          ]),
        ])}
      />

      <PageHeader
        overline="Biography · Long Read"
        title="From Santo Domingo"
        italic="to social commentary."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid grid-cols-12 gap-8 lg:gap-16">
          {/* MAIN COLUMN */}
          <article className="col-span-12 lg:col-span-8">
            {/* Lede */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-[#0a0a0a] text-2xl md:text-3xl leading-snug tracking-[-0.01em]"
            >
              <span className="float-left font-display text-[88px] md:text-[120px] leading-[0.8] tracking-tighter pr-3 -mt-1 text-[#C8102E]">M</span>
              anuel Santana is a Dominican-American media personality, podcast
              host, author, and commentator focused on modern dating,
              relationships, gender dynamics, and culture.
            </motion.p>

            {/* Origins */}
            <div className="mt-14">
              <Section overline="01 · Origins">
                <p>
                  Manuel was born on <strong className="text-[#0a0a0a]">October 1, 1987</strong>, in <strong className="text-[#0a0a0a]">Santo Domingo, Dominican Republic</strong>,
                  entering the world alongside his identical twin brother, <strong className="text-[#0a0a0a]">Miguel Santana</strong>.
                  Less than two years later, in the summer of 1989, their family immigrated
                  to <strong className="text-[#0a0a0a]">New York City</strong> in search of greater opportunities and a better future.
                </p>
                <p>
                  Growing up in the Northeast exposed Manuel to one of the most diverse and influential
                  cultural environments in the world. Surrounded by music, entrepreneurship, ambition,
                  and the fast-paced energy of city life, he developed an early fascination with
                  entertainment, communication, and the power of influence.
                </p>
              </Section>
            </div>

            {/* Music */}
            <div className="mt-14">
              <Section overline="02 · The Music Years">
                <p>
                  Like many young men growing up in the Northeast during the rise of hip-hop culture,
                  Manuel dreamed of becoming a successful recording artist. Alongside his twin
                  brother Miguel, he spent years pursuing a career in music — writing songs,
                  recording tracks, performing, and working toward the dream of building a name
                  in the entertainment industry.
                </p>
              </Section>
            </div>

            {/* Atlanta */}
            <div className="mt-14">
              <Section overline="03 · Atlanta &amp; The Nightlife Education">
                <p>
                  After graduating from high school, the Santana family relocated to <strong className="text-[#0a0a0a]">Atlanta</strong>.
                  While continuing to pursue music, Manuel was introduced to an entirely different
                  world — nightlife.
                </p>
                <p>
                  It was in Atlanta that he first became involved in nightclub promotion and
                  nightclub security. What initially began as a way to earn income and stay
                  connected to the entertainment scene eventually became something much more
                  significant.
                </p>
                <p>
                  Working in nightlife placed Manuel in a unique position. Night after night,
                  year after year, he observed thousands of social interactions unfold in real
                  time. He watched how men approached women, how attraction developed, how
                  relationships formed, and how misunderstandings often destroyed connections.
                  He witnessed confidence, insecurity, success, rejection, loyalty, infidelity,
                  heartbreak, and commitment play out in front of him on a daily basis.
                </p>

                {/* Pull quote */}
                <motion.blockquote
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="my-10 pl-6 md:pl-10 border-l-2 border-[#C8102E]"
                >
                  <p className="font-display text-[#0a0a0a] text-2xl md:text-4xl leading-[1.2] tracking-[-0.02em]">
                    "While many people experienced dating from only their own perspective, Manuel
                    found himself observing the dating world from the outside looking in."
                  </p>
                </motion.blockquote>

                <p>
                  Over time, these observations sparked a deep curiosity about human behavior,
                  attraction, relationship dynamics, and the factors that influence how men and
                  women connect with one another.
                </p>
              </Section>
            </div>

            {/* Inline portrait */}
            <div className="mt-16">
              <div className="portrait-frame contain w-full aspect-[3/4] sm:aspect-[4/5]">
                <img src={ASSETS.podcastPortrait} alt="Manuel Santana — portrait" draggable={false} />
              </div>
              <p className="mt-3 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Portrait № 421 · The Prove Your Point Podcast</p>
            </div>

            {/* Boston */}
            <div className="mt-14">
              <Section overline="04 · The Boston Chapter">
                <p>
                  In the winter of <strong className="text-[#0a0a0a]">2013</strong>, Manuel relocated back to the Northeast, settling in
                  the <strong className="text-[#0a0a0a]">Boston</strong> area for family reasons. The move marked the beginning of a new chapter.
                </p>
                <p>
                  Although music had been a major passion for many years, the doors he hoped would
                  open in the entertainment industry ultimately led him down a different path.
                  Rather than viewing those setbacks as failures, Manuel saw them as <em className="not-italic text-[#0a0a0a] font-semibold">redirections</em>.
                </p>
                <p>
                  The experiences he gained through music, nightlife, and years of observing people
                  from all walks of life had given him something valuable: perspective.
                </p>
              </Section>
            </div>

            {/* Mission */}
            <div className="mt-14">
              <Section overline="05 · Finding The Mission">
                <p>
                  As his focus shifted away from pursuing fame through music, he became increasingly
                  interested in understanding and discussing the challenges facing modern
                  relationships. He began studying dating dynamics, relationship psychology,
                  communication patterns, social behavior, and the growing disconnect between men
                  and women in contemporary society.
                </p>
                <p className="font-display text-[#0a0a0a] text-2xl md:text-3xl leading-snug tracking-[-0.01em]">
                  What started as personal curiosity evolved into a mission.
                </p>
                <p>
                  Manuel recognized that many of the conversations people wanted to have about
                  dating, relationships, accountability, expectations, and modern culture were
                  either being avoided altogether or discussed only from one side of the debate.
                  He believed there was room for more honest dialogue — conversations that
                  challenged assumptions, encouraged critical thinking, and gave people an
                  opportunity to understand perspectives different from their own.
                </p>
              </Section>
            </div>

            {/* Podcast */}
            <div className="mt-14">
              <Section overline="06 · The Prove Your Point Podcast">
                <p>
                  This vision eventually led to the creation of{" "}
                  <Link to="/podcast" className="link-underline text-[#0a0a0a] font-medium">The Prove Your Point Podcast</Link>,
                  a platform dedicated to exploring modern dating, relationships, gender dynamics,
                  culture, and social issues through open discussion and debate.
                </p>
                <p>
                  The podcast was built on a simple idea: meaningful conversations happen when
                  people are willing to listen, question, and engage with viewpoints beyond their own.
                </p>
              </Section>
            </div>

            {/* Book */}
            <div className="mt-14">
              <Section overline="07 · The 12 Laws of Dating Dynamics">
                <p>
                  In addition to podcasting, Manuel is the author of{" "}
                  <Link to="/books" className="link-underline text-[#0a0a0a] font-medium">The 12 Laws of Dating Dynamics</Link>,
                  a book that examines relationship power balances, attraction, communication, and
                  the challenges people face in modern dating.
                </p>
                <p>
                  His work aims to help individuals better understand themselves, their partners,
                  and the social forces that influence relationships.
                </p>
              </Section>
            </div>

            {/* Today */}
            <div className="mt-14">
              <Section overline="08 · Today">
                <p>
                  Today, Manuel Santana continues to build his platform as a media personality,
                  author, and podcast host. Through his writing, interviews, and discussions, he
                  remains committed to encouraging thoughtful conversations about relationships,
                  culture, personal growth, and the evolving dynamics between men and women.
                </p>
                <p className="font-display text-[#0a0a0a] text-2xl md:text-3xl leading-snug tracking-[-0.01em] pt-4">
                  His mission is not to tell people what to think, but to create a space where
                  important conversations can happen — conversations that help people better
                  understand each other and ultimately build stronger, healthier relationships.
                </p>
              </Section>
            </div>

            {/* CTAs */}
            <div className="mt-20 pt-12 border-t border-black/10 flex flex-wrap gap-4">
              <Link to="/podcast" className="btn-primary">
                Listen To The Podcast <ArrowRight size={16} />
              </Link>
              <Link to="/books" className="btn-ghost">
                Read The Book
              </Link>
              <Link to="/contact" className="btn-ghost">
                Book An Interview
              </Link>
            </div>
          </article>

          {/* SIDEBAR */}
          <aside className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-10">
              <div className="portrait-frame w-full aspect-[4/5]">
                <img src={ASSETS.heroPortrait} alt="Manuel Santana" draggable={false} />
              </div>

              <div className="border border-black/15 p-6 md:p-7">
                <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E]">— Quick Facts</p>
                <dl className="mt-6 space-y-4">
                  {FACTS.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 gap-3 border-b border-black/8 pb-3 last:border-b-0 last:pb-0">
                      <dt className="col-span-1 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-black/45">{k}</dt>
                      <dd className="col-span-2 text-sm text-[#0a0a0a]">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="border border-black/15 p-6 md:p-7">
                <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E]">— For Journalists</p>
                <p className="mt-4 text-sm text-black/70 leading-relaxed">
                  Pre-written bios at 50, 100 and 250 words plus high-resolution headshots and logos.
                </p>
                <Link to="/press-kit" className="mt-5 inline-flex items-center gap-2 border border-black/30 hover:bg-[#0a0a0a] hover:text-white px-4 py-2.5 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition-all">
                  Open Press Kit <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-white py-20 md:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#C8102E]">— Timeline</p>
          <h2 className="mt-5 font-display text-[#0a0a0a] text-[clamp(32px,5vw,60px)] leading-[1.05] tracking-[-0.02em]">
            One life, four cities, <span className="font-light text-black/45">one mission.</span>
          </h2>

          <ol className="mt-14 border-t border-black/10">
            {TIMELINE.map((t, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 border-b border-black/10 items-start"
              >
                <span className="col-span-3 md:col-span-2 font-display text-2xl md:text-3xl text-[#C8102E] tracking-tight">{t.year}</span>
                <span className="col-span-9 md:col-span-3 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[#0a0a0a] mt-2">{t.city}</span>
                <span className="col-span-12 md:col-span-7 text-black/70 text-base md:text-lg leading-relaxed">{t.event}</span>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-white py-20 md:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#C8102E]">— What's Next</p>
            <h2 className="mt-5 font-display text-[#0a0a0a] text-[clamp(36px,6vw,76px)] leading-[1] tracking-[-0.02em]">
              The conversation
              <br />
              <span className="font-light text-black/45">keeps going.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-black/65 text-base md:text-lg leading-relaxed">
              Hear it weekly on The Prove Your Point Podcast, read it in The 12 Laws of Dating
              Dynamics, or send Manuel a note directly.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:text-right">
            <Link to="/contact" className="btn-primary">
              Get In Touch <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
